import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../client';
import { qk } from './keys';
import type { PaginatedResponse, Tour, TourPurchase, TourPurchaseOrIntent } from '../types';
import { withQuery } from '@/utils/url';

export type TourListParams = {
  type?: 'local' | 'international';
  country_code?: string;
  cursor?: string;
  limit?: number;
  [k: string]: string | number | undefined;
};

export function useTours(params: TourListParams = {}) {
  return useQuery({
    queryKey: qk.tours(params),
    queryFn: () => apiGet<PaginatedResponse<Tour>>(withQuery('/tours', params)),
  });
}

export function useTourDetails(id: string | null) {
  return useQuery({
    queryKey: qk.tourDetails(id ?? ''),
    queryFn: () => apiGet<Tour>(`/tours/${id ?? ''}`),
    enabled: !!id,
  });
}

export function usePurchaseTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payWith }: { id: string; payWith?: 'wallet' | 'card' }) =>
      apiPost<TourPurchaseOrIntent>(
        `/tours/${id}/purchase`,
        { pay_with: payWith ?? 'wallet', idempotency_key: crypto.randomUUID() },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.wallet });
      qc.invalidateQueries({ queryKey: qk.tourPurchases });
    },
  });
}

export function useTourPurchases() {
  return useQuery({
    queryKey: qk.tourPurchases,
    queryFn: () => apiGet<TourPurchase[]>('/tour-purchases'),
  });
}
